import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIError, APIResponse } from "@/types/custom-http";
import {
  CertificationDetails,
  CertificationView,
  CreateCertificationRequest,
  UpdateCertificationRequest,
  RejectCertificationRequest,
  VoidCertificationRequest,
} from "./certifications.interface";
import { certificationsApi } from "./certificationsApi";

export const fetchCertificationsByWorkOrder = createAsyncThunk<
  APIResponse<CertificationView[]>,
  number,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/fetchByWorkOrder", async (workOrderId, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.listCertificationsByWorkOrder.initiate(workOrderId)
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al obtener certificaciones",
      details: data.details,
    });
  }
});

export const fetchCertificationById = createAsyncThunk<
  APIResponse<CertificationDetails>,
  number,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/fetchById", async (id, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.getCertificationById.initiate(id)
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al obtener certificación",
      details: data.details,
    });
  }
});

export const createCertification = createAsyncThunk<
  APIResponse<CertificationDetails>,
  CreateCertificationRequest,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/create", async (data, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.createCertification.initiate(data)
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al crear certificación",
      details: data.details,
    });
  }
});

export const updateCertificationDraft = createAsyncThunk<
  APIResponse<CertificationDetails>,
  { id: number; changes: UpdateCertificationRequest },
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/updateDraft", async ({ id, changes }, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.updateCertificationDraft.initiate({ id, changes })
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al actualizar certificación",
      details: data.details,
    });
  }
});

export const deleteCertificationDraft = createAsyncThunk<
  APIResponse<null>,
  number,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/deleteDraft", async (id, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.deleteCertificationDraft.initiate(id)
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al eliminar certificación",
      details: data.details,
    });
  }
});

export const submitCertification = createAsyncThunk<
  APIResponse<null>,
  { id: number },
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/submit", async ({ id }, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.submitCertification.initiate({
        id,
      })
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al enviar certificación",
      details: data.details,
    });
  }
});

export const approveCertification = createAsyncThunk<
  APIResponse<null>,
  { id: number },
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/approve", async ({ id }, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.approveCertification.initiate({
        id,
      })
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al aprobar certificación",
      details: data.details,
    });
  }
});

export const rejectCertification = createAsyncThunk<
  APIResponse<null>,
  { id: number; body: RejectCertificationRequest },
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/reject", async ({ id, body }, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.rejectCertification.initiate({ id, body })
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al rechazar certificación",
      details: data.details,
    });
  }
});

export const voidCertification = createAsyncThunk<
  APIResponse<null>,
  { id: number; body: VoidCertificationRequest },
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("certifications/void", async ({ id, body }, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(
      certificationsApi.endpoints.voidCertification.initiate({ id, body })
    ).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al anular certificación",
      details: data.details,
    });
  }
});
